import { FormProvider, useForm } from "react-hook-form"
import getInputValidationClasses from "@/lib/helpers/getInputValidationClasses"
import { input } from "@/common/form/input.module.css"
import InputGroup from "@/common/form/inputGroup"
import Label from "@/common/form/label"
import { ErrorMessage } from "../errorMessage"
import useCheckoutContext, { useRecruiterData } from "../store"
import SubmitStepButton from "../user-details/submitStepButton"
import { useEffect, useState, useRef } from "react"
import { F2FLogin, F2FLogout } from "@/lib/api/f2f"
import Storage from "@/lib/storage"
import Modal from "react-modal"
import Personnummer from "personnummer"
import styles from "@/components/base/popup/popup.module.css"
import Close from "@/components/common/icons/close"
import Button from "@/components/common/button/button"
import { InputDescription } from "@/components/common/form/input"
import ReCAPTCHA from "react-google-recaptcha"

export default function RecruiterID() {
  const { setNextActiveStep } = useCheckoutContext()
  const { recruiterData, setRecruiterData } = useRecruiterData()
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [loginError, setLoginError] = useState(false)
  const [loginPinError, setLoginPinError] = useState(false)
  const [loginDisabled, setLoginDisabled] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const f2f_memberId = useRef(null)
  const f2f_pin = useRef(null)
  const f2f_entrycode = useRef(null)

  const recaptchaRef = useRef()
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY
  const statusTimer = useRef(null)

  const entryCodes = [
    { value: "46", label: "Street Team Halmstad", active: false },
    { value: "45", label: "Street Team Malmö", active: false },
    { value: "44", label: "Street Team Stockholm", active: true },
    { value: "43", label: "Street Team Göteborg", active: true },
    { value: "15", label: "Event", active: true },
    { value: "13", label: "D2D", active: false },
    { value: "12", label: "Street", active: false }
  ]

  useEffect(() => {
    // Check if the user is logged in
    if (Storage.get("f2f-memberId")) {
      setValue("recruiterId", Storage.get("f2f-memberId"))
      setValue("entryCode", Storage.get("f2f-entryCode"))
      setIsLoggedIn(true)
      startTimer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function startTimer() {
    setTimeLeft()
    statusTimer.current = setInterval(function () {
      setTimeLeft()
    }, 60000)
  }

  function setTimeLeft() {
    let startTime = Storage.get("f2f-loginStamp")
    let secondsLeftValue = (startTime - Date.now()) / 1000

    if (secondsLeftValue < 0) {
      setShowLogoutModal(true)
      logoutUser()
    }

    setSecondsLeft(secondsLeftValue)
  }

  function stopTimer() {
    clearInterval(statusTimer.current)
  }

  function formatSeconds(seconds) {
    let d = Number(seconds)
    var h = Math.floor(d / 3600)
    var m = Math.floor((d % 3600) / 60)
    return h + " tim, " + m + " minuter"
  }

  const methods = useForm({
    defaultValues: {
      ...recruiterData
    },
    mode: "onTouched"
  })

  const { setValue } = methods

  const { handleSubmit, register, control } = methods

  const onSubmit = (values) => {
    setRecruiterData(values)
    setNextActiveStep()
  }

  async function loginUser() {
    setLoginError(false)
    setLoginPinError(false)
    setIsSubmitting(true)

    if (!Personnummer.valid(f2f_pin.current.value)) {
      setLoginPinError(true)
      setIsSubmitting(false)
      return
    }

    const captcha = await recaptchaRef.current.executeAsync()
    const login = await F2FLogin(
      f2f_pin.current.value,
      f2f_memberId.current.value,
      f2f_entrycode.current.value,
      captcha
    )

    if (login.isValid) {
      Storage.set("f2f-memberId", f2f_memberId.current.value)
      Storage.set("f2f-entryCode", f2f_entrycode.current.value)
      Storage.set("f2f-sessionId", login.sessionId)
      Storage.set("f2f-loginStamp", Date.now() + 3600 * 6750)
      setValue("recruiterId", Storage.get("f2f-memberId"))
      setValue("entryCode", Storage.get("f2f-entryCode"))
      setIsLoggedIn(true)
      setModalIsOpen(false)

      startTimer()
    } else {
      setLoginError(true)
    }
    setLoginDisabled(false)
    setIsSubmitting(false)
  }

  async function logoutUser() {
    await F2FLogout(Storage.get("f2f-sessionId"))

    setValue("recruiterId", "")
    setValue("entryCode", "12")
    Storage.remove("f2f-memberId")
    Storage.remove("f2f-entryCode")
    Storage.remove("f2f-sessionId")
    Storage.remove("f2f-loginStamp")
    setIsLoggedIn(false)
    setLoginDisabled(true)
    stopTimer()
  }

  const customStyles = {
    overlay: {
      display: "flex",
      backgroundColor: "rgba(0, 0, 0, .5)",
      zIndex: 1000,
      alignItems: "center",
      justifyContent: "center"
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal
        isOpen={modalIsOpen}
        style={customStyles}
        shouldReturnFocusAfterClose={true}
        shouldCloseOnOverlayClick={true}
        className={styles.content}>
        <div className={styles.click} onClick={() => setModalIsOpen(false)}>
          <div className={styles.close}>
            <Close className="icon" />
          </div>
        </div>
        <div className="content-wrapper">
          <h2>Logga in</h2>
          <p className="mb-4">
            Logga in med ditt personnummer och ditt medlemsnummer
          </p>

          <InputGroup>
            <Label>Inträdeskod</Label>
            <div className="relative w-full">
              <select
                ref={f2f_entrycode}
                onChange={(value) =>
                  setLoginDisabled(Boolean(!value.target.value))
                }
                className="block w-full py-2 pl-4 pr-8 text-lg leading-tight bg-white border rounded appearance-none border-black-25 focus:outline-none focus:shadow-outline">
                <option value="">Välj team</option>
                {entryCodes
                  .filter((code) => code.active)
                  .map((code) => (
                    <option key={code.value} value={code.value}>
                      {code.label}
                    </option>
                  ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none">
                <svg
                  className="w-4 h-4 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </InputGroup>

          <InputGroup>
            <Label>Personnummer</Label>
            <input
              type="text"
              placeholder="ååååmmddxxxx"
              className={[input]}
              ref={f2f_pin}
            />
            <InputDescription>
              Ange ditt personnummer med 12 siffror
            </InputDescription>
            {loginPinError && (
              <div className="mt-1 text-xs text-red-100">
                Ange ett korrekt personnummer
              </div>
            )}
          </InputGroup>

          <InputGroup>
            <Label>Medlemsnummer</Label>
            <input type="text" className={[input]} ref={f2f_memberId} />
          </InputGroup>

          <div className="flex items-center mt-6">
            <Button
              disabled={loginDisabled || isSubmitting}
              ariaLabel="Logga In"
              theme="green"
              className="mr-4"
              loading={isSubmitting}
              onClick={() => loginUser()}>
              {isSubmitting ? "Loggar in..." : "Logga in"}
            </Button>

            {loginError && (
              <div className="mt-1 text-xs text-red-100">
                Felaktiga uppgifter, försök igen.
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ReCAPTCHA
        className="hidden"
        ref={recaptchaRef}
        size="invisible"
        sitekey={siteKey}
      />

      {!isLoggedIn && (
        <Button
          ariaLabel="Logga in"
          onClick={() => {
            setModalIsOpen(true)
          }}
          className="absolute px-6 py-3 ml-auto text-base font-bold no-underline bg-green-100 border border-black border-solid rounded-md cursor-pointer top-6 right-6">
          Logga in
        </Button>
      )}

      {isLoggedIn && (
        <Button
          ariaLabel="Logga ut"
          onClick={() => {
            logoutUser()
          }}
          className="absolute px-3 py-1 ml-auto text-base text-white no-underline border border-black border-solid rounded-md cursor-pointer bg-black-100 top-6 right-6">
          Logga ut
        </Button>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoggedIn && (
          <>
            <InputGroup>
              <Label required={true} name="recruiterId">
                Rekryterar-ID
              </Label>
              <input
                id="recruiterId"
                type="number"
                disabled={isLoggedIn}
                className={[
                  input,
                  getInputValidationClasses("recruiterId", control)
                ].join(" ")}
                {...register("recruiterId", {
                  required: true
                })}
              />
              <ErrorMessage name="recruiterId" />
            </InputGroup>
            <InputGroup>
              <Label required={true} name="entryCode">
                Välj inträdeskod
              </Label>
              <div className="relative w-full">
                <select
                  disabled={isLoggedIn}
                  id="entryCode"
                  defaultValue=""
                  {...register("entryCode")}
                  className="block w-full py-2 pl-4 pr-8 text-lg leading-tight bg-white border border-green-100 rounded appearance-none focus:outline-none focus:shadow-outline">
                  {entryCodes
                    .filter((code) => code.active)
                    .map((code) => (
                      <option key={code.value} value={code.value}>
                        {code.label}
                      </option>
                    ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none">
                  <svg
                    className="w-4 h-4 fill-current"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </InputGroup>

            {secondsLeft && (
              <p className="mb-6">
                Tid kvar till utloggning:{" "}
                <strong>{formatSeconds(secondsLeft)}</strong>
              </p>
            )}

            <SubmitStepButton />
          </>
        )}
      </form>

      <Modal
        isOpen={showLogoutModal}
        style={customStyles}
        shouldReturnFocusAfterClose={false}
        shouldCloseOnOverlayClick={false}
        className={styles.content}>
        <div className="text-center content-wrapper">
          <h2>Du har blivit utloggad</h2>
          <div>
            <Button
              theme="lime"
              ariaLabel="Stäng"
              className="mx-4 button"
              onClick={() => {
                setShowLogoutModal(false)
              }}>
              Stäng
            </Button>
          </div>
        </div>
      </Modal>
    </FormProvider>
  )
}
